import { Injectable, ErrorHandler } from '@angular/core';
import { AppEnvironment } from 'environments/environment';
import * as Sentry from '@sentry/angular';
import { isLocalEnv, isCapacitorNative, isE2ETest } from 'environments/environment-helpers';
import { IS_TEST_TRAFFIC_PROPERTY } from '@cf-shared/telemetry-shared';
import { getUnversionedRelayOrigin } from 'util/backend-origin';
import { getSentryReleaseVersion } from 'environments/version-getters';
import { LoggingService, isIdbConnectionError, isIdbError, isClosedDatabaseHandleError, isTransientFetchError, isFirebaseAuthNetworkError, isCouchHttpErrorObject, isThirdPartyError } from 'services/logging.service';
import { DatabaseRecoveryService } from 'services/database-recovery.service';
import { getAppFrameOrigin, isRefinerLoadError, isPouchConflictError } from 'services/error-patterns';
import { isDatabaseUnavailable } from 'services/database-unavailable-error';
import { applyDefaultProblemKindTag, MixpanelCopy } from 'services/problem-kind';
import { realTimerEnvironment, TimerEnvironment } from 'services/running-clock';
import { redactCredentialsInBreadcrumb } from './breadcrumb-redaction';
import { reportDatabaseUnavailableError } from './sentry-error-policy';

// This file has three places that catch an error and decide what happens to it: Sentry's
// `beforeSend` hook, the `unhandledrejection` listener, and `SentryErrorHandler.handleError`.
// They each keep their OWN list of filters on purpose, and that is not an oversight waiting to
// be merged (deferred finding df-57f1a22751fe proposed merging them, and was worked and closed
// with the answer below).
//
// They cannot share one list because a shared RULE does not imply a shared JOB. A transient fetch
// error in `beforeSend` means "drop the Sentry event"; the same error in the rejection listener
// means "stop Chrome printing an Uncaught-in-promise line". And only `beforeSend` sees a parsed
// Sentry event at all, which is what the CouchDB regroup below rewrites.
//
// What IS shared is written down once and imported: the predicates live in
// `services/error-patterns.ts`, and any policy that two entry points genuinely both perform lives
// in `sentry-error-policy.ts`.
//
// Importing this file does NOTHING (deferred finding df-106e22b8ff13). Every effect — configuring
// the SDK, tagging a native session, registering the rejection listener — happens inside
// `initSentry()` at the bottom, which `app-sentry-init.ts` calls once. The three hooks are
// exported functions rather than closures written into the `Sentry.init` argument, for the same
// reason: it is what lets `app-sentry.test.ts` run them one at a time.

// Track event IDs dropped by beforeSend so LoggingService can skip the toast.
// beforeSend runs synchronously inside captureException/captureMessage, so the
// set is populated before captureException returns the (orphaned) event ID.
const droppedEventIds = new Set<string>();
export function wasEventDropped(eventId: string): boolean {
  return droppedEventIds.delete(eventId);
}

// set by SentryErrorHandler once Angular DI is available
let l_loggingService: LoggingService | null = null;
let l_databaseRecovery: DatabaseRecoveryService | null = null;

/** Test-only: forget the two services `SentryErrorHandler`'s constructor stored above.
 *
 * Constructing that handler is the only way to reach this module state, so a test that wants the
 * hooks to see a service has to construct one — and the fakes it wires in would otherwise outlive
 * the test file, in the module the whole mocha process shares. `app-sentry.test.ts` calls this in
 * its `after()`. Nothing in the app calls it. */
export function resetSentryServicesForTests(): void {
  l_loggingService = null;
  l_databaseRecovery = null;
}

// #3992: provider hook for the connection_state tag. OnlineStateService
// registers a callback here once Angular DI constructs it — this Sentry setup
// runs before DI exists, so it cannot inject the service itself.
// Until registration (or if it never happens), events are tagged 'unknown'.
let connectionStateProvider: (() => string) | null = null;
export function registerSentryConnectionStateProvider(provider: () => string) {
  connectionStateProvider = provider;
}

// df-a0223e8ffec8: provider hook for the is_test_traffic tag, the same shape and for
// the same reason as the connection_state one above. `LoggingService.initialize`
// registers a callback once it has been handed the telemetry service, which is what
// actually knows the answer; it is registered from THERE rather than from
// `TelemetryService`'s own constructor because `logging.service.ts` already imports
// this module (for `wasEventDropped`), so this adds no import edge that did not exist.
//
// Until registration, `sentryBeforeSend` falls back to reading the E2E session flag
// itself. See the comment at that call for why the fallback is not redundant.
let testTrafficProvider: (() => boolean) | null = null;
export function registerSentryTestTrafficProvider(provider: (() => boolean) | null) {
  testTrafficProvider = provider;
}

// df-2bb6e71820c2: on iOS, a promise sometimes rejects with `null` (or `undefined`) just after
// the app wakes from a long background freeze, and nothing handles it. A null reason carries no
// message and no stack, so the SDK's own report of it names no source. We do not know which code
// rejects, so instead of guessing, the report is given what IS known at that moment: whether the
// page was hidden, and how long it had just been frozen. A follow-up report then says how many
// such rejections arrived together, which tells a repeating timer apart from a single failure.
// Why, and what the two reports look like: design-docs/warm-resume.md, "Null unhandled rejections".

/** How often the page notes that it is running. A gap between two notes far longer than this
 * means the page was frozen. */
const PAGE_TICK_MS = 1_000;
/** A gap between two ticks longer than this is recorded as a freeze rather than a busy moment. */
const PAGE_FREEZE_GAP_MS = 5_000;
/** How long after the first null rejection the follow-up report counting them is filed. */
const NULL_REJECTION_BURST_WINDOW_MS = 10_000;

interface PageActivity {
  lastTickAtMs: number | null;
  lastFreezeMs: number | null;
  lastFreezeEndedAtMs: number | null;
  lastVisibilityChangeAtMs: number | null;
  lastVisibilityChangeTo: string | null;
}
interface NullRejectionBurst {
  count: number;
  /** the id of the SDK's own event for this burst, recorded by beforeSend, which runs first */
  sentryEventId: string | null;
  timerArmed: boolean;
}

function freshPageActivity(): PageActivity {
  return { lastTickAtMs: null, lastFreezeMs: null, lastFreezeEndedAtMs: null, lastVisibilityChangeAtMs: null, lastVisibilityChangeTo: null };
}
function freshNullRejectionBurst(): NullRejectionBurst {
  return { count: 0, sentryEventId: null, timerArmed: false };
}

// `performance.now()` in production: a monotonic clock, as RunningClock explains
let pageTimers: TimerEnvironment = realTimerEnvironment();
let pageActivity = freshPageActivity();
let nullRejectionBurst = freshNullRejectionBurst();

/** Test-only: forget every page-activity and null-rejection reading, and use the given clock and
 * timers (the real ones when none are given). Nothing in the app calls it. */
export function resetNullRejectionTrackingForTests(timers?: TimerEnvironment): void {
  pageTimers = timers ?? realTimerEnvironment();
  pageActivity = freshPageActivity();
  nullRejectionBurst = freshNullRejectionBurst();
}

/** Called every `PAGE_TICK_MS` by the timer `initSentry()` starts. Exported so a test can run it;
 * nothing else calls it. */
export function notePageTick(): void {
  const now = pageTimers.nowMs();
  // if this tick came far later than it was due, the page was frozen in between
  if (pageActivity.lastTickAtMs !== null && now - pageActivity.lastTickAtMs > PAGE_FREEZE_GAP_MS) {
    pageActivity.lastFreezeMs = now - pageActivity.lastTickAtMs;
    pageActivity.lastFreezeEndedAtMs = now;
  }
  pageActivity.lastTickAtMs = now;
}

/** The `visibilitychange` listener `initSentry()` registers. Exported so a test can run it;
 * nothing else calls it. */
export function notePageVisibilityChange(visibilityState: string): void {
  pageActivity.lastVisibilityChangeAtMs = pageTimers.nowMs();
  pageActivity.lastVisibilityChangeTo = visibilityState;
}

/** What is known right now about whether the page was hidden or frozen. `null` means "never
 * observed", which is itself an answer worth reporting. */
function pageActivitySnapshot(): Record<string, string | number | null> {
  const now = pageTimers.nowMs();
  const msSince = (at: number | null) => at === null ? null : Math.round(now - at);
  return {
    page_visibility: typeof document !== 'undefined' ? document.visibilityState : 'unknown',
    last_visibility_change_to: pageActivity.lastVisibilityChangeTo,
    ms_since_visibility_change: msSince(pageActivity.lastVisibilityChangeAtMs),
    // far above PAGE_TICK_MS means the page has only just woken and no tick has run since
    ms_since_last_page_tick: msSince(pageActivity.lastTickAtMs),
    last_freeze_ms: pageActivity.lastFreezeMs === null ? null : Math.round(pageActivity.lastFreezeMs),
    ms_since_last_freeze_ended: msSince(pageActivity.lastFreezeEndedAtMs),
  };
}

/** A rejection reason that carries nothing at all to diagnose it by. */
function isEmptyRejectionReason(reason: unknown): boolean {
  return reason === null || reason === undefined;
}

/** file the follow-up report for the null rejections counted since the first one */
function reportNullRejectionBurst(): void {
  const { count, sentryEventId } = nullRejectionBurst;
  nullRejectionBurst = freshNullRejectionBurst();
  const context = { count, sentry_event_id: sentryEventId, ...pageActivitySnapshot() };
  // if logging is not ready yet, file nothing: the Sentry event this follows up is still the record
  if (!l_loggingService) return;
  // Sentry only: its rate is read in Sentry next to the event it follows up
  l_loggingService.trackSilentInfo('UnhandledNullRejectionBurst', context, MixpanelCopy.Skip);
}

/** open the burst window if it is not open yet. Either hook may see a burst first, and both must
 * be able to close it, or a window opened by one would stay open for ever. */
function openNullRejectionWindow(): void {
  if (nullRejectionBurst.timerArmed) return;
  nullRejectionBurst.timerArmed = true;
  pageTimers.setTimeout(reportNullRejectionBurst, NULL_REJECTION_BURST_WINDOW_MS);
}

/** suppress a Sentry event if any exception value matches the predicate */
function suppressSentryEvent(
  event: Sentry.Event,
  predicate: (ex: Sentry.Exception) => boolean,
  label: string,
): boolean {
  if (!event.exception?.values?.some(predicate)) return false;
  console.log(`Sentry: suppressed ${label}`);
  if (event.event_id) droppedEventIds.add(event.event_id);
  return true;
}

/** Sibling of `suppressSentryEvent` for a rule stated over the RAW thrown value rather than over
 * the parsed exception values. `errorMessage` is tested as well as the value itself because the
 * Sentry SDK enriches some messages (it turns "Failed to fetch" into "Failed to fetch
 * (hostname:port)"), so the raw object and the reported text do not always both match. */
function suppressSentryEventForRawError(
  event: Sentry.Event,
  originalError: unknown,
  errorMessage: string,
  predicate: (value: unknown) => boolean,
  label: string,
): boolean {
  if (!predicate(originalError) && !predicate(errorMessage)) return false;
  console.log(`Sentry: suppressed ${label}:`, errorMessage);
  if (event.event_id) droppedEventIds.add(event.event_id);
  return true;
}

/** The mechanism the SDK's GlobalHandlers integration stamps on an event it filed for an
 * unhandled promise rejection. */
const UNHANDLED_REJECTION_MECHANISM = 'auto.browser.global_handlers.onunhandledrejection';

/** Which SDK mechanism produced this event. The hint carries it for an event the SDK captured by
 * itself; the event's first exception value carries the same thing once the SDK has prepared it,
 * so that is read when the hint has none. */
function sentryMechanismType(event: Sentry.ErrorEvent, hint?: Sentry.EventHint): string | undefined {
  return hint?.mechanism?.type ?? event.exception?.values?.[0]?.mechanism?.type;
}

/** The `beforeBreadcrumb` hook, handed to `Sentry.init` by `initSentry()` below. Exported so a
 * test can run it; nothing else calls it.
 *
 * Disables automatic console logging, which we do manually in LoggingService. */
export function sentryBeforeBreadcrumb(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb | null {
  // ignore breadcrumbs from the console _that are auto-generated by Sentry_ (we create our own, which don't have the logger set)
  if (breadcrumb.category === 'console' && breadcrumb.data && breadcrumb.data.logger === 'console') {
    return null;
  } else {
    // everything else is kept, so strip any credential out of it first: a signed GCS URL
    // (deferred finding df-7e8cc723bc5b) or a Discourse SSO login address (#4525). Both are
    // addresses whose query string is a working credential. See breadcrumb-redaction.ts.
    return redactCredentialsInBreadcrumb(breadcrumb);
  }
}

/** The `beforeSend` hook, handed to `Sentry.init` by `initSentry()` below. Exported so a test can
 * run it; nothing else calls it.
 *
 * Intercepts IDB connection errors that escape as unhandled promise rejections from PouchDB
 * internals (e.g. finishBatch -> src.info -> idb.transaction when iOS Safari closes IDB in
 * background), and decides for every other event whether it is reported, dropped or rewritten.
 *
 * `hint` is optional here although the SDK always passes one, so a test can call this with an
 * event alone. A function taking an optional argument still satisfies the SDK's type. */
export function sentryBeforeSend(event: Sentry.ErrorEvent, hint?: Sentry.EventHint): Sentry.ErrorEvent | null {
  // #3992: tag every event with the app's connection state, so triage can
  // slice errors by offline/online. Set before the filters below — even a
  // rewritten event (e.g. the CouchDB regroup) must carry the tag.
  //
  // df-a0223e8ffec8: and tag every event with whether this session is TEST TRAFFIC, so
  // a report the E2E suite provokes on purpose can be told apart from a real user's.
  // Two things about it are easy to "tidy" wrongly, so they are here rather than only in
  // the doc. The tag is ALWAYS present and reads `true` or `false`, never set-only-when-
  // true. And the `?? isE2ETest()` fallback is NOT redundant with the provider's own
  // reading of the same flag: it covers the boot window before
  // `LoggingService.initialize` registers anything, which is when a test run that fell
  // over early reports. Why each of those matters, and the measurements:
  // design-docs/telemetry.md, "Test-traffic exclusion rule".
  event.tags = {
    ...event.tags,
    connection_state: connectionStateProvider?.() ?? 'unknown',
    [IS_TEST_TRAFFIC_PROPERTY]: testTrafficProvider?.() ?? isE2ETest(),
  };

  // #4525: redact the event's OWN page address as well as its breadcrumbs. beforeBreadcrumb
  // above covers breadcrumbs only, and the Discourse forum sends a user back to our page with
  // `?sso=…&sig=…` still in the address — a working forum login, which Sentry attaches to the
  // event by itself with no console call involved. Reusing the breadcrumb redactor keeps one
  // definition of what a credential looks like; it reads `message` and `data`, so the address is
  // handed to it as a message and the redacted text read back.
  if (typeof event.request?.url === 'string') {
    event.request.url = redactCredentialsInBreadcrumb({ message: event.request.url }).message!;
  }

  // #4382: make problem_kind a COMPLETE partition of every Sentry event. The three
  // LoggingService tiers tag their own events; everything else reaching Sentry (the
  // Angular ErrorHandler, unhandled promise rejections, whatever the SDK captures on
  // its own) is a real error and is tagged as one here. Without this default those
  // events would carry no tag at all, and a saved search written as "real problems
  // only" would silently drop exactly the events that matter most.
  applyDefaultProblemKindTag(event.tags);

  const originalError = hint?.originalException;
  let detectedIdb = false;

  // check the raw error object first (covers both indexed_db_went_bad name and IDB message patterns)
  if (originalError && typeof originalError === 'object') {
    detectedIdb = isIdbError(originalError);
  }

  // fallback: check Sentry event exception values (string-only, can't check error.name)
  if (!detectedIdb && event.exception?.values) {
    for (const ex of event.exception.values) {
      if (ex.value && isIdbConnectionError(ex.value)) {
        detectedIdb = true;
        break;
      }
    }
  }

  // fallback: check Sentry event message (for captureMessage events
  // from trackAbnormalError/trackSilentProblem that contain IDB errors)
  if (!detectedIdb && typeof event.message === 'string') {
    detectedIdb = isIdbConnectionError(event.message);
  }

  if (detectedIdb) {
    // trigger recovery if Angular DI is available
    if (l_databaseRecovery) {
      l_databaseRecovery.interceptDatabaseError(
        originalError || { message: 'IDB connection error (from beforeSend)', name: 'indexed_db_went_bad' }
      );
    }
    // suppress the Sentry event -- recovery system handles its own telemetry
    if (event.event_id) droppedEventIds.add(event.event_id);
    return null;
  }

  const errorMessage = (originalError as any)?.message
    || event.exception?.values?.[0]?.value
    || '';

  // df-25af43842f0f: drop the SDK's own error-level copy of a refused database read, which our
  // rejection listener reports through the logging service once that is registered. Any other
  // capture of it is kept. Why, and the early-boot case that keeps it: design-docs/pass-through-db.md.
  if (isDatabaseUnavailable(originalError)
    && l_loggingService
    && sentryMechanismType(event, hint) === UNHANDLED_REJECTION_MECHANISM) {
    console.log('Sentry: suppressed SDK duplicate of an unhandled database refusal:', errorMessage);
    if (event.event_id) droppedEventIds.add(event.event_id);
    return null;
  }

  // filter transient fetch/network errors (offline, AbortController timeout, Safari backgrounding)
  if (suppressSentryEventForRawError(event, originalError, errorMessage,
    isTransientFetchError, 'transient fetch error',
  )) return null;

  // filter transient Firebase auth network errors (auth/network-request-failed) — the
  // app handles auth offline gracefully (deferred couch login + focus/online retry);
  // the Firebase token-refresh / auth-iframe path otherwise escapes as an unhandled
  // rejection (Sentry triage v1.25.0 Group 3, issue 450).
  if (suppressSentryEventForRawError(event, originalError, errorMessage,
    isFirebaseAuthNetworkError, 'Firebase auth network error',
  )) return null;

  // A raw PouchDB/CouchDB HTTP error object (e.g. {status:404,name:'not_found',...}, or the
  // 401/403 equivalents) is a plain object, not an Error, so when one escapes a fire-and-forget
  // call as an unhandled rejection Sentry has no message/stack to group on — every cause
  // collapses into one undiagnosable `<unknown>` group (Sentry triage v1.25.0 Group 12, issue
  // 2JB). Rewrite it into a real error grouped by (status, name) so it becomes diagnosable.
  // We KEEP reporting it (do not drop): a 403/404 can be a genuine signal and the volume is
  // tiny. Placed before the third-party filter so the rewrite always applies (these events
  // have no parsed stacktrace).
  if (isCouchHttpErrorObject(originalError)) {
    const { status, name } = originalError;
    const detail = originalError.reason || originalError.message || originalError.error || '';
    if (event.exception?.values?.[0]) {
      event.exception.values[0].type = 'UnhandledCouchRejection';
      event.exception.values[0].value =
        `Unhandled CouchDB rejection: ${status} ${name}${detail ? ` (${detail})` : ''}`;
    }
    event.fingerprint = ['unhandled-couch-rejection', String(status), String(name)];
    event.extra = {
      ...(event.extra || {}),
      couch_status: status,
      couch_name: name,
      couch_reason: originalError.reason,
      couch_error: originalError.error,
    };
    console.log(`Sentry: regrouped unhandled CouchDB rejection ${status} ${name}`);
    return event;
  }

  // df-2bb6e71820c2: an unhandled rejection whose reason is null or undefined. It brings no
  // message and no stack, so give it a name, one group, and what is known about whether the
  // page had just been hidden or frozen. KEPT, like the CouchDB regroup above: its source is
  // still unknown. Only the SDK's own unhandled-rejection capture is rewritten.
  //
  // ONE event per burst window, and the rest are dropped. The SDK's Dedupe cannot do that here:
  // it keeps the very object we rewrite as "the previous event", so the next identical SDK event
  // no longer matches it, and a timer failing every 800 ms would file an event each time. The
  // listener below still counts every one, and the follow-up report carries the count.
  if (hint !== undefined && isEmptyRejectionReason(originalError)
    && sentryMechanismType(event, hint) === UNHANDLED_REJECTION_MECHANISM) {
    // if this window already reported an event, this one is a repeat of it. Not added to
    // droppedEventIds: only LoggingService's own captures read that set back, and these never
    // come from there, so every id would stay in it for the life of the page.
    if (nullRejectionBurst.sentryEventId !== null) return null;
    openNullRejectionWindow();
    if (event.exception?.values?.[0]) {
      event.exception.values[0].type = 'UnhandledNullRejection';
      event.exception.values[0].value = `Unhandled promise rejection with a ${String(originalError)} reason`;
    }
    event.fingerprint = ['unhandled-null-rejection'];
    event.extra = { ...(event.extra || {}), ...pageActivitySnapshot() };
    // the follow-up report counting this burst names this event, so the two can be joined
    nullRejectionBurst.sentryEventId = event.event_id ?? null;
    return event;
  }

  // filter errors from third-party/external scripts (browser extensions, injected scripts)
  // that have no app code in their stacktrace. The page's own address has to be passed
  // in: on the phone apps Capacitor serves our page from the device, so our own stack
  // lines carry `https://localhost/...` (Android) or `capacitor://localhost/...` (iOS)
  // rather than our website's address (#4546).
  if (isThirdPartyError(event, getAppFrameOrigin(window.location))) {
    console.warn('Sentry: suppressed third-party error:', event.exception?.values?.[0]?.type, event.exception?.values?.[0]?.value);
    if (event.event_id) droppedEventIds.add(event.event_id);
    return null;
  }

  // Instagram/WebView on iOS freezes certain properties as readonly; third-party checkout JS
  // (PayPro) triggers TypeError when calling Object.defineProperty. No real app code involved.
  if (suppressSentryEvent(event, ex =>
    ex.type === 'TypeError'
    && ex.value?.includes('Attempting to change value of a readonly property'),
    'WebView readonly property error',
  )) return null;

  // PouchDB conflict errors are self-healing (merge resolution handles them)
  // (also filtered in SentryErrorHandler.handleError as a first line of defense)
  if (suppressSentryEvent(event, isPouchConflictError,
    'PouchDB conflict error',
  )) return null;

  // Refiner CDN load failures from ad-blockers — non-functional, self-healing
  if (suppressSentryEvent(event, ex => isRefinerLoadError(ex.value),
    'Refiner CDN load error',
  )) return null;

  // Capacitor native bridge internal errors — no app frames, unhandled rejections from native layer
  if (suppressSentryEvent(event, ex => ex.value?.includes('Object Not Found Matching Id'),
    'Capacitor bridge error',
  )) return null;

  return event;
}

/** keep a rejection out of Chrome's console if its reason matches the predicate. Does NOT stop the
 * listener: a rejection is offered to every guard below, the way it was before this was a helper. */
function suppressRejection(
  event: PromiseRejectionEvent,
  predicate: (reason: unknown) => boolean,
  label: string,
): void {
  if (!predicate(event.reason)) return;
  console.log(`Suppressed ${label}:`, event.reason?.message || event.reason);
  event.preventDefault();
}

/** The `window` `unhandledrejection` listener, registered by `initSentry()` below. Exported so a
 * test can run it; nothing else calls it.
 *
 * Prevents Chrome from logging transient fetch errors as SEVERE "Uncaught (in promise)" console
 * entries — PouchDB retry: true guarantees these are retried automatically. */
export function handleSentryUnhandledRejection(event: PromiseRejectionEvent): void {
  // df-2bb6e71820c2: count every null or undefined reason, and file one follow-up report a short
  // while after the first. Counted here rather than in beforeSend because beforeSend keeps only
  // one event per window. Not claimed: the SDK's own event stays the primary record, and
  // preventDefault() could not stop it anyway.
  if (isEmptyRejectionReason(event.reason)) {
    nullRejectionBurst.count++;
    openNullRejectionWindow();
    return;
  }
  suppressRejection(event, isTransientFetchError, 'transient fetch rejection');
  // Firebase auth network errors (auth/network-request-failed) from the auth-iframe /
  // token-refresh path surface as unhandled rejections — known transient, app handles
  // auth offline (Sentry triage v1.25.0 Group 3, issue 450)
  suppressRejection(event, isFirebaseAuthNetworkError, 'Firebase auth network rejection');
  // downgrade Refiner CDN load failures (ad-blockers, content blockers) to console
  suppressRejection(event, isRefinerLoadError, 'Refiner CDN load rejection');
  // while we are destroying databases on purpose, pending PouchDB ops reject — expected, not a failure
  // mode. Suppress so they don't surface as unhandled rejections (and so the E2E handler below skips
  // them). This covers BOTH kinds of intentional destruction, and they do NOT reject the same way, which
  // is exactly why the guard tests isIdbError() rather than matching a message: a purge
  // (purgeDatabaseAsync / restoreFromDocsAsync) destroys through localDb.destroy(), so its pending ops
  // reject with "database is closed"; the sign-out wipe closes nothing — it deletes the IndexedDB store
  // out from under a still-open connection, so its ops reject with a dying-connection error instead. For
  // the wipe the suppression lasts until the page reloads, which is deliberate — the whole teardown is
  // destruction, and a reload is guaranteed to end it.
  if (l_databaseRecovery?.isIntentionalDestruction() && isIdbError(event.reason)) {
    event.preventDefault();
    return;
  }
  // A call that arrived on a store handle we had already closed, outside the purge and the
  // sign-out wipe that the guard above covers. Those are not the only ways a database stops
  // being the live one: a second app start-up builds a new DatabaseWrapper and shuts the
  // previous one down, and anything still holding the old wrapper that calls it afterwards gets
  // "database is closed" from PouchDB's own post-close guard. That case reached here owned by
  // nobody, so it stayed an unhandled rejection — which Chrome reports and the E2E gate reads
  // as a browser error. Owning it is the same treatment, for the same reason, that
  // isDatabaseUnavailable gets in the block just below.
  //
  // `isClosedDatabaseHandleError` is deliberately narrower than `isIdbError`: that also matches
  // genuine device faults, which must keep being treated as real failures.
  //
  // The reporting is NOT done here on purpose: interceptDatabaseError owns this condition, so we
  // hand the rejection over rather than filing our own event. It reaches that method twice
  // whatever we do — preventDefault() does not unregister Sentry's own listener, so beforeSend
  // above calls it again with the same object — and it dedupes. Why it owns it, and the E2E
  // ignore-list note that comes with it: design-docs/boot-sequence.md, "A call on a handle we
  // closed ourselves is not a broken IndexedDB".
  if (isClosedDatabaseHandleError(event.reason)) {
    event.preventDefault();
    if (l_databaseRecovery) {
      l_databaseRecovery.interceptDatabaseError(event.reason, 'unhandled-rejection');
    } else {
      // the handler can fire before SentryErrorHandler's constructor has set l_databaseRecovery.
      // Never suppress into total silence.
      console.error('Call on a closed database before logging was ready:', event.reason?.message);
    }
    return;
  }
  // A read that refused because the database was not available (deferred #2 —
  // every DatabaseWrapper read now throws rather than answering with an empty
  // result). It is NOT an isIdbError, so the guard above does not cover it —
  // that gap is what made the original "shutdown is already suppressed"
  // assumption wrong, and it let this reach trackAbnormalException, which shows
  // the user an error toast. What happens next is the same here as in
  // SentryErrorHandler.handleError, so it lives in sentry-error-policy.ts.
  //
  // preventDefault() does not stop the SDK's own rejection handler from filing the same error a
  // second time, as an error-level exception. beforeSend above drops that copy once the logging
  // service is registered (df-25af43842f0f).
  if (isDatabaseUnavailable(event.reason)) {
    event.preventDefault();
    reportDatabaseUnavailableError(event.reason, l_databaseRecovery, l_loggingService);
    return;
  }
}

/** Start Sentry: configure the SDK, tag a native session, and register the rejection listener.
 *
 * This is the ONLY thing in this file with an effect, and importing the file no longer performs
 * it (deferred finding df-106e22b8ff13). That is what lets a test load the module and run the
 * hooks above one at a time. Called once, from `app-sentry-init.ts`, which `main.ts` imports
 * FIRST so that this runs before the rest of the app's module graph evaluates.
 *
 * Calling it twice would configure the SDK twice and register a second listener. Nothing needs
 * to, so nothing guards against it. */
export function initSentry(): void {
  Sentry.init({
    dsn: 'https://690342c4a07e42b7b2026feef69d9bee@o202844.ingest.us.sentry.io/1317290',
    // Captured ONCE, when this runs, and never refreshed — Sentry reads the
    // tunnel out of these options for the life of the client. So this is the one
    // backend URL in the app that genuinely cannot wait for anything, which is
    // why it uses the relay accessor (#4405).
    //
    // That is safe because the tunnel does not care which server relays it: /api/e
    // forwards the envelope to whatever Sentry ingest host the envelope's own DSN
    // names, so the event reaches the same Sentry project either way. On a native
    // prod boot with a cached route this is already the routed host; on a
    // first-ever boot it is the live host, and the events land in the same place.
    tunnel: `${getUnversionedRelayOrigin()}/api/e`,
    environment: AppEnvironment.name,
    release: getSentryReleaseVersion(),
    enabled: !isLocalEnv(), // don't report errors to the server during development
    maxBreadcrumbs: 1000,

    // No `integrations` / `defaultIntegrations` here on purpose: @sentry/angular's own
    // init supplies a default list that leaves out BrowserApiErrors, which would wrap
    // window.setTimeout and break the `timer_native` field of
    // BootReadPriorityFailsafeFired (app.component.ts, #5204). Break it SILENTLY: the
    // FunctionToString integration in that same list makes a wrapped function stringify
    // as the one it wrapped, so the field would keep reading `true`. If you add an
    // integration that wraps timers, fix that field in the same change. Why it matters:
    // design-docs/boot-sequence.md, "#4616".

    // #3992 (offline-mode Phase 4, F16): queue events raised while offline in an
    // IndexedDB store (default db name 'sentry-offline', default maxQueueSize 30)
    // and flush them on the first successful send after reconnect. Without this,
    // errors from offline sessions — exactly where offline bugs live — were
    // dropped. The offline transport wraps the fetch transport and honors the
    // tunnel URL above. flushAtStartup stays off on purpose: boot is busy
    // enough, and the queue flushes on the first successful send anyway.
    transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),

    beforeBreadcrumb: sentryBeforeBreadcrumb,
    beforeSend: sentryBeforeSend,
  });

  if (isCapacitorNative()) {
    Sentry.setTag('app_native', true);
  }

  window.addEventListener('unhandledrejection', handleSentryUnhandledRejection);

  // df-2bb6e71820c2: keep the page-activity readings the null-rejection report carries
  notePageTick();
  setInterval(notePageTick, PAGE_TICK_MS);
  document.addEventListener('visibilitychange', () => notePageVisibilityChange(document.visibilityState));
}

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  constructor(private logging: LoggingService, private databaseRecovery: DatabaseRecoveryService) {
    // make both services available to the module-level hooks above, which run outside Angular
    // DI and so cannot inject anything themselves.
    //
    // `logging` is listed FIRST on purpose. Angular builds a constructor's dependencies in
    // order, and `LoggingService` is what hands itself to `DatabaseRecoveryService`; taking it
    // first means that handover has already happened by the time we store either of them.
    l_loggingService = logging;
    l_databaseRecovery = databaseRecovery;
  }
  handleError(error: any) {
    // if the error has been wrapped by the zone.js, then we need to unwrap it
    if (error && error.rejection) {
      error = error.rejection;
    }

    // for IDB errors
    if (this.databaseRecovery.interceptDatabaseError(error, 'angular-error-handler')) {
      return;
    }

    // A DatabaseWrapper read that refused because the database was not
    // available: either our own teardown or a transient recovery, so it is
    // reported quietly rather than as an abnormal exception with a user-facing
    // toast. The unhandledrejection listener above does exactly the same thing
    // with it, which is why the decision lives in sentry-error-policy.ts.
    if (isDatabaseUnavailable(error)) {
      reportDatabaseUnavailableError(error, this.databaseRecovery, this.logging);
      return;
    }

    // PouchDB conflict errors are self-healing — replication and saveRecordInternalAsync
    // both handle conflicts via merge resolution. Suppress from Sentry.
    // (also filtered in beforeSend as a safety-net for rejections that bypass ErrorHandler)
    if (isPouchConflictError(error)) {
      console.log('Suppressed PouchDB conflict error:', error.message);
      return;
    }

    // if this looks like results of net::ERR_BLOCKED_BY_CLIENT
    if ('headers' in error && error.ok === false && error.status === 0) {
      // this suspects that the error is due to adblocker
      this.logging.trackAdBlocker(error.url, "from ErrorHandler", error);
      return;
    }

    // ignore internal exceptions used for page guards
    if (error.message && error.message.includes('Redirecting due to unsafe page params')) {
      return;
    }

    this.logging.trackAbnormalException(error.originalError || error);
    //debugger;
  }
}
